/** @jsx isolateComponent */
import { Children } from 'react';
import { css } from '@emotion/core';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { Typography } from '../../../theme';
import { Icon } from '../../display/Icon';

type Props = {
  /**
   * The `Link`s to render within `Breadcrumbs`. They will be wrapped in
   * an `ol > li` structure for accessibility purposes.
   */
  children: React.ReactNode;
};

const Styles = labelStyles('Breadcrumbs', {
  root: css`
    ${Typography.smallPrint1};
  `,
  list: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 2px 5px;
  `,
  item: css`
    display: flex;
    align-items: inherit;
    gap: inherit;
  `,
});

/**
 * Navigation for rendering a shallow or deep hierarchy of pages
 */
export const Breadcrumbs = (props: Props) => {
  const items = Children.toArray(props.children)
    .filter(Boolean)
    .map((link: React.ReactNode, i, links) => {
      const icon = i !== links.length - 1 && (
        <span aria-hidden="true">
          <Icon
            isBlock={true}
            color="gray40"
            name="arrowSmallRight"
            size={18}
          />
        </span>
      );
      // Using `i` for `key` since there isn't anything else great
      // to use. Since `Breadcrumbs` should have a consistent hierarchy,
      // this should be fine. Consumers can change the key on `Breadcrumbs`
      // to force it to remount if necessary.
      return (
        <li css={Styles.item} key={i}>
          {link}
          {icon}
        </li>
      );
    });
  return (
    <div css={Styles.root}>
      <ol css={Styles.list}>{items}</ol>
    </div>
  );
};
